import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export const DEFAULT_TOPICS = ["fundraising", "growth", "ops", "product", "africa"] as const;

interface Props {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  topics?: string[];
  onTopicsChange?: (next: string[]) => void;
  showTopics?: boolean;
  className?: string;
  /** override consent label (e.g. shorter copy for compact surfaces) */
  label?: string;
}

/**
 * Reusable, GDPR-friendly newsletter opt-in (checkbox + optional topic chips).
 * Pure controlled component — host form decides when to call newsletter-subscribe.
 */
export function NewsletterOptIn({
  checked,
  onCheckedChange,
  topics = [],
  onTopicsChange,
  showTopics = false,
  className,
  label,
}: Props) {
  const { t } = useTranslation();
  const toggleTopic = (topic: string) => {
    if (!onTopicsChange) return;
    const set = new Set(topics);
    set.has(topic) ? set.delete(topic) : set.add(topic);
    onTopicsChange(Array.from(set));
  };

  return (
    <div className={cn("space-y-3", className)}>
      <label className="flex items-start gap-2 cursor-pointer">
        <Checkbox
          checked={checked}
          onCheckedChange={(v) => onCheckedChange(v === true)}
          className="mt-0.5"
        />
        <span className="t-body-sm text-text-3">
          {label ?? t("newsletter.optIn.label", "Send me the Bootpreneurs newsletter — practical playbooks, no spam.")}
        </span>
      </label>

      {showTopics && checked && (
        <div className="space-y-1.5 pl-6">
          <p className="t-body-sm text-text-3">{t("newsletter.optIn.topicsLabel", "I care about:")}</p>
          <div className="flex flex-wrap gap-1.5">
            {DEFAULT_TOPICS.map((topic) => {
              const active = topics.includes(topic);
              return (
                <button
                  key={topic}
                  type="button"
                  onClick={() => toggleTopic(topic)}
                  className="focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-full"
                >
                  <Badge variant={active ? "default" : "outline"} className="cursor-pointer capitalize">
                    {t(`newsletter.topics.${topic}`, topic)}
                  </Badge>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}